import { useMemo, useState } from 'react'
import { useQuery } from 'urql'
import { WarningCircleIcon } from '@phosphor-icons/react'
import type { Workflow } from 'app/generated/workflows'
import { Button } from 'app/components/ui/button'
import ComboMultiSelect from 'app/components/ui/combo/combo-multi-select'
import { Switch } from 'app/components/ui/switch'
import {
  type DeployLocation,
  type DeployOutcome,
  type DeployTarget,
  deployTemplates,
  deployedWorkflowName,
  planDeployment,
} from 'app/lib/workflows/deployTemplates'
import { LIST_WORKFLOWS_QUERY, WORKFLOW_LOCATIONS_QUERY } from 'app/lib/workflows/graphql'
import type { WorkflowTemplate } from 'app/lib/workflows/workflowTemplates'
import { notifySuccess } from 'app/services/notify.client'

type Phase =
  | { kind: `idle` }
  | { kind: `running`; done: number; total: number }
  /** At least one pair did not fully land. `outcomes` is cumulative across retries. */
  | { kind: `failed`; outcomes: DeployOutcome[] }

type DeployTemplatesFooterProps = {
  /** The templates ticked on the system screen. */
  templates: WorkflowTemplate[]
  /** Whether the current user may apply a template's texting category at the engine (supers). */
  canSetCategory: boolean
  /** Every requested pair landed, or the user has accepted the failures. */
  onDeployed: (outcomes: DeployOutcome[]) => void
}

const noun = (count: number, singular: string, plural: string) => (count === 1 ? singular : plural)

/** The bottom of the system-templates screen once something is ticked: choose locations, deploy the
 *  ticked templates to every one of them in a single motion, and see what did not land. */
export function DeployTemplatesFooter({ templates, canSetCategory, onDeployed }: DeployTemplatesFooterProps) {
  const [{ data: locationData }] = useQuery({
    query: WORKFLOW_LOCATIONS_QUERY,
    variables: { pagination: { limit: 100 } },
  })
  // Fresh on every open: a stale list would let a second pass re-create what the first just made.
  // Same page size as the workflows list, which is the ceiling this guard can see.
  const [{ data: workflowData }] = useQuery<{ workflows: { hits: Workflow[] } }>({
    query: LIST_WORKFLOWS_QUERY,
    variables: { pagination: { limit: 100 }, sort: { field: `updatedDate`, direction: `DESC` } },
    requestPolicy: `network-only`,
  })
  const locations = (locationData?.locations?.hits ?? []) as DeployLocation[]
  const existing = workflowData?.workflows?.hits ?? []

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [activate, setActivate] = useState(true)
  const [phase, setPhase] = useState<Phase>({ kind: `idle` })

  const plan = useMemo(
    () =>
      planDeployment(
        templates,
        locations.filter((location) => selectedIds.has(location.id)),
        existing,
      ),
    [templates, locations, selectedIds, existing],
  )

  // The picker wants option objects, and re-seeds itself whenever `values` changes identity, so both
  // lists are memoised rather than rebuilt on every render.
  const locationOptions = useMemo(
    () => locations.map((location) => ({ id: location.id, value: location.id, name: location.name ?? location.id })),
    [locations],
  )
  const selectedOptions = useMemo(
    () => locationOptions.filter((option) => selectedIds.has(option.id)),
    [locationOptions, selectedIds],
  )

  const run = async (targets: DeployTarget[], carried: DeployOutcome[]) => {
    setPhase({ kind: `running`, done: 0, total: targets.length })
    const fresh = await deployTemplates(targets, {
      activate,
      canSetCategory,
      onProgress: (done, total) => setPhase({ kind: `running`, done, total }),
    })
    // A retry replaces the outcome of each pair it re-ran and keeps everything else as it was.
    const rerun = new Set(targets)
    const outcomes = [...carried.filter((outcome) => !rerun.has(outcome.target)), ...fresh]
    if (outcomes.some((outcome) => outcome.error)) {
      setPhase({ kind: `failed`, outcomes })
      return
    }
    notifySuccess(`Deployed ${outcomes.length} ${noun(outcomes.length, `workflow`, `workflows`)}`)
    onDeployed(outcomes)
  }

  if (phase.kind === `failed`) {
    const failures = phase.outcomes.filter((outcome) => outcome.error)
    // Only a pair that never got a server id is safe to run again. One that was created and then
    // failed to activate would come back as a second copy.
    const retryable = failures.filter((outcome) => !outcome.workflowId).map((outcome) => outcome.target)
    return (
      <footer
        data-testid="deploy-templates-footer"
        className="flex flex-col gap-2 rounded-xl border border-red-200 bg-red-50/40 p-4"
      >
        <div className="flex items-center gap-1.5 text-xs font-semibold text-red-700">
          <WarningCircleIcon size={14} weight="bold" />
          {`${failures.length} of ${phase.outcomes.length} failed`}
        </div>
        <ul className="max-h-32 overflow-y-auto text-[11px] text-default">
          {failures.map((outcome) => (
            <li key={`${outcome.target.template.id}@${outcome.target.location.id}`} className="flex gap-2 py-0.5">
              <span className="shrink-0 font-medium">
                {deployedWorkflowName(outcome.target.template, outcome.target.location)}
              </span>
              <span className="text-subtle">{outcome.error}</span>
            </li>
          ))}
        </ul>
        <div className="flex justify-end gap-2">
          <Button variant="white" size="sm" onClick={() => onDeployed(phase.outcomes)}>
            Done
          </Button>
          {retryable.length > 0 && (
            <Button size="sm" onClick={() => run(retryable, phase.outcomes)}>
              Retry failed
            </Button>
          )}
        </div>
      </footer>
    )
  }

  const running = phase.kind === `running`
  const skipped = plan.skipped.length
  return (
    <footer
      data-testid="deploy-templates-footer"
      className="flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50/40 p-4"
    >
      <div>
        <p className="text-xs font-semibold text-default">Deploy to locations</p>
        <p className="text-[11px] text-subtle">
          One workflow is created per template per location, named for the location it runs for.
        </p>
      </div>
      <div className="flex items-center gap-3">
        <div className="w-72">
          <ComboMultiSelect
            name="deploy-locations"
            options={locationOptions}
            values={selectedOptions}
            placeholder="Choose locations…"
            disabled={running}
            checkboxSide="left"
            onChange={(options) => setSelectedIds(new Set(options.map((option) => option.id)))}
          />
        </div>
        <Button
          variant="link"
          size="xs"
          disabled={running || locations.length === 0}
          onClick={() => setSelectedIds(new Set(locations.map((location) => location.id)))}
        >
          {`All ${locations.length} ${noun(locations.length, `location`, `locations`)}`}
        </Button>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-[11px] text-subtle">
            {templates.length} {noun(templates.length, `template`, `templates`)} · {selectedIds.size}
            {` `}
            {noun(selectedIds.size, `location`, `locations`)} · {plan.targets.length}
            {` `}
            {noun(plan.targets.length, `workflow`, `workflows`)}
          </span>
          {skipped > 0 && <span className="text-[11px] text-minimal">{skipped} already deployed, skipped</span>}
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch id="deploy-activate" checked={activate} disabled={running} onCheckedChange={setActivate} />
            <label htmlFor="deploy-activate" className="cursor-pointer text-xs text-default">
              Activate on deploy
            </label>
          </div>
          <Button
            size="sm"
            disabled={plan.targets.length === 0 || running}
            loading={running}
            onClick={() => run(plan.targets, [])}
          >
            {running
              ? `Deploying ${phase.done} of ${phase.total}`
              : `Deploy ${plan.targets.length} ${noun(plan.targets.length, `workflow`, `workflows`)}`}
          </Button>
        </div>
      </div>
    </footer>
  )
}
